// @catalog F.006
// MenuDrawer — rediseño dark theme ejecutivo plan dashboard_executive_redesign 2026-05-21.
// Variant "permanent" en desktop (fijo, sin overlay) o "temporary" en mobile.
// Preserva lógica completa de permisos + estructura de menús + sub-menús + colapsables.
import React, { useState } from "react";
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Home as HomeIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  AccountBox as AccountBoxIcon,
  CloudSync as CloudSyncIcon,
  Sync as SyncIcon,
  Analytics as AnalyticsIcon,
  LocalMall as PurchaseIcon,
  TrendingUp as ForecastIcon,
  AttachMoney as MarginIcon,
  Warning as WarningStockIcon,
  Category as CategoryIcon,
  Sell as BrandIcon,
  LocalShipping as LocalShippingIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  CompareArrows as CompareArrowsIcon,
  Block as BlockIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
  Send as SendIcon,
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  Download as DownloadIcon,
  Storefront as StorefrontIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { sigMxService, type SigMxDumpVersion } from "../services/sigMxService";

export const SIDEBAR_WIDTH = 240;

interface MenuDrawerProps {
  onToggle?: (open: boolean) => void;
  isOpen?: boolean;
  // Permanente en desktop (sin overlay, fijo) o temporal en mobile (slide-in).
  variant?: "permanent" | "temporary";
}

interface MenuItemDef {
  title: string;
  icon: React.ReactElement;
  path: string;
  route: string;
  menu: string;
  ruta?: string;
  menuKey?: string;
  funcion?: string;
  requiresPermission?: boolean;
}

interface SidebarItemProps {
  active: boolean;
  icon: React.ReactElement;
  label: string;
  badge?: string | number;
  nested?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onClick: () => void;
}

function SidebarItem(props: SidebarItemProps): React.ReactElement {
  const { active, icon, label, badge, nested, expandable, expanded, onClick } = props;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: nested ? "6px 12px 6px 36px" : "7px 12px",
        margin: "0 4px",
        borderRadius: "6px",
        backgroundColor: active ? "#1d2540" : "transparent",
        color: active ? "#fff" : "#cbd1de",
        border: "none",
        fontFamily: "inherit",
        fontSize: "13px",
        fontWeight: 450,
        cursor: "pointer",
        textAlign: "left",
        width: "calc(100% - 8px)",
        transition: "background-color 0.15s ease, color 0.15s ease",
        "&:hover": active
          ? {}
          : { backgroundColor: "#1a2033", color: "#fff" },
        "&:focus-visible": {
          outline: "2px solid var(--accent)",
          outlineOffset: "-2px",
        },
        "& svg": {
          fontSize: 16,
          opacity: active ? 1 : 0.75,
          color: active ? "var(--accent, var(--brand-blue-2))" : "currentColor",
        },
        ...(active && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: -4,
            top: 7,
            bottom: 7,
            width: "2px",
            backgroundColor: "var(--accent, var(--brand-blue))",
            borderRadius: "0 2px 2px 0",
          },
        }),
      }}
    >
      {icon}
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
      {badge !== undefined && (
        <Box
          component="span"
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            padding: "1px 6px",
            backgroundColor: "var(--neg)",
            color: "#fff",
            borderRadius: "10px",
          }}
        >
          {badge}
        </Box>
      )}
      {expandable &&
        (expanded ? (
          <ExpandLess sx={{ fontSize: 14, opacity: 0.6 }} />
        ) : (
          <ExpandMore sx={{ fontSize: 14, opacity: 0.6 }} />
        ))}
    </Box>
  );
}

function SidebarSection({ label }: { label: string }): React.ReactElement {
  return (
    <Box
      sx={{
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: "#5b6578",
        padding: "14px 12px 6px",
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
  );
}

export function MenuDrawer({
  onToggle,
  isOpen = false,
  variant = "temporary",
}: MenuDrawerProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useUser();
  const [usuariosOpen, setUsuariosOpen] = useState(false);
  const [sigMxOpen, setSigMxOpen] = useState(false);
  // Sig-mx: descarga en curso (bloquea doble click) y feedback al usuario
  const [sigMxBusy, setSigMxBusy] = useState<SigMxDumpVersion | null>(null);
  const [sigMxSnackbar, setSigMxSnackbar] = useState<{
    message: string;
    severity: "success" | "info" | "warning" | "error";
  } | null>(null);
  const [ventasOpen, setVentasOpen] = useState(true); // expandido por default en desktop
  const [comprasOpen, setComprasOpen] = useState(false);
  const [odooOpen, setOdooOpen] = useState(false);
  const [distribucionOpen, setDistribucionOpen] = useState(false);
  const [reportesDistribucionOpen, setReportesDistribucionOpen] = useState(false);

  // === Definición de menús — preservada literal del Layout anterior ===
  const menuItems: MenuItemDef[] = [
    {
      title: "Inicio",
      icon: <HomeIcon />,
      path: "/",
      route: "/",
      menu: "inicio",
      requiresPermission: false,
    },
  ];

  const comprasItems: MenuItemDef[] = [
    {
      title: "Forecast",
      icon: <ForecastIcon />,
      path: "/compras/forecast",
      route: "/compras/forecast",
      menu: "compras-forecast",
      ruta: "/compras",
      menuKey: "forecast",
      funcion: "ver",
    },
    {
      title: "Marcas",
      icon: <BrandIcon />,
      path: "/compras/marcas",
      route: "/compras/marcas",
      menu: "compras-forecast",
      ruta: "/compras",
      menuKey: "forecast",
      funcion: "ver",
    },
    {
      title: "Categorías",
      icon: <CategoryIcon />,
      path: "/compras/categorias/revision",
      route: "/compras/categorias/revision",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
  ];

  const distribucionItems: MenuItemDef[] = [
    {
      title: "Generar transferencias",
      icon: <SendIcon />,
      path: "/compras/distribucion/transferencias",
      route: "/compras/distribucion/transferencias",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Inventario apertura",
      icon: <InventoryIcon />,
      path: "/compras/distribucion/apertura",
      route: "/compras/distribucion/apertura",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "editar",
    },
    {
      title: "Configuración",
      icon: <SettingsIcon />,
      path: "/compras/distribucion/configuracion",
      route: "/compras/distribucion/configuracion",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
  ];

  const reportesDistribucionItems: MenuItemDef[] = [
    {
      title: "Cobertura por tienda",
      icon: <DashboardIcon />,
      path: "/compras/distribucion",
      route: "/compras/distribucion",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Historial transferencias",
      icon: <HistoryIcon />,
      path: "/compras/distribucion/historial",
      route: "/compras/distribucion/historial",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Backlog",
      icon: <InventoryIcon />,
      path: "/compras/distribucion/backlog",
      route: "/compras/distribucion/backlog",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Denegaciones",
      icon: <BlockIcon />,
      path: "/compras/distribucion/denegaciones",
      route: "/compras/distribucion/denegaciones",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Diferencias",
      icon: <CompareArrowsIcon />,
      path: "/compras/distribucion/diferencias",
      route: "/compras/distribucion/diferencias",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Alertas Subcompra",
      icon: <WarningStockIcon />,
      path: "/compras/distribucion/alertas-subcompra",
      route: "/compras/distribucion/alertas-subcompra",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
    {
      title: "Métricas Éxito",
      icon: <AnalyticsIcon />,
      path: "/compras/distribucion/metricas",
      route: "/compras/distribucion/metricas",
      menu: "compras-distribucion",
      ruta: "/compras",
      menuKey: "distribucion",
      funcion: "ver",
    },
  ];

  const ventasItems: MenuItemDef[] = [
    {
      title: "Ventas",
      icon: <AnalyticsIcon />,
      path: "/ventas",
      route: "/ventas",
      menu: "ventas",
      ruta: "/ventas",
      menuKey: "ventas",
      funcion: "ver",
    },
    {
      title: "Márgenes",
      icon: <MarginIcon />,
      path: "/margenes",
      route: "/margenes",
      menu: "margenes",
      ruta: "/ventas",
      menuKey: "margenes",
      funcion: "ver",
    },
    {
      title: "Ventas B2B",
      icon: <AnalyticsIcon />,
      path: "/ventas-b2b",
      route: "/ventas-b2b",
      menu: "clientes",
      ruta: "/ventas-b2b",
      menuKey: "clientes",
      funcion: "ver",
    },
  ];

  // Mercado Libre (F.047): sección propia — techo del dominio ML para las
  // features futuras (billing API, asientos). ADR 2026_08_21.
  const mercadoLibreItems: MenuItemDef[] = [
    {
      title: "Importaciones",
      icon: <StorefrontIcon />,
      path: "/mercado-libre",
      route: "/mercado-libre",
      menu: "importacion",
      ruta: "/mercado-libre",
      menuKey: "importacion",
      funcion: "ver",
    },
    {
      title: "Resultados",
      icon: <AssessmentIcon />,
      path: "/mercado-libre/resultados",
      route: "/mercado-libre",
      menu: "importacion",
      ruta: "/mercado-libre",
      menuKey: "importacion",
      funcion: "ver",
    },
    {
      title: "Resumen mensual",
      icon: <MarginIcon />,
      path: "/mercado-libre/resumen",
      route: "/mercado-libre",
      menu: "importacion",
      ruta: "/mercado-libre",
      menuKey: "importacion",
      funcion: "ver",
    },
  ];

  const usuariosItems: MenuItemDef[] = [
    {
      title: "Gestión de Usuarios",
      icon: <PeopleIcon />,
      path: "/usuarios",
      route: "/usuarios",
      menu: "gestion-usuarios",
      ruta: "/usuarios",
      menuKey: "gestion-usuarios",
      funcion: "ver",
    },
    {
      title: "Gestión de Grupos",
      icon: <GroupIcon />,
      path: "/grupos",
      route: "/grupos",
      menu: "gestion-grupos",
      ruta: "/usuarios",
      menuKey: "gestion-grupos",
      funcion: "ver",
    },
    {
      title: "Gestión de Permisos",
      icon: <SecurityIcon />,
      path: "/permisos",
      route: "/permisos",
      menu: "gestion-permisos",
      ruta: "/usuarios",
      menuKey: "gestion-permisos",
      funcion: "ver",
    },
  ];

  const odooItems: MenuItemDef[] = [
    {
      title: "Sincronización Remota",
      icon: <SyncIcon />,
      path: "/odoo/remote-sync",
      route: "/odoo/remote-sync",
      menu: "odoo-remote-sync",
      ruta: "/odoo-erp",
      menuKey: "remote-sync",
      funcion: "ver",
    },
    {
      title: "Migración Producción",
      icon: <CloudSyncIcon />,
      path: "/odoo/production-sync",
      route: "/odoo/production-sync",
      menu: "migration-produccion",
      ruta: "/odoo-erp",
      menuKey: "migration-produccion",
      funcion: "ver",
    },
  ];

  // Sig-mx: links de descarga de las bases odoo (no navegan — descargan).
  // odoo_16 es copia estática; odoo_18 se regenera tras cada production-sync.
  const sigMxItems: Array<{
    title: string;
    icon: React.ReactElement;
    version: SigMxDumpVersion;
    ruta: string;
    menuKey: string;
    funcion: string;
  }> = [
    {
      title: "Descargar odoo_18",
      icon: <DownloadIcon />,
      version: "odoo_18",
      ruta: "/sig-mx",
      menuKey: "descargas",
      funcion: "ver",
    },
    {
      title: "Descargar odoo_16",
      icon: <DownloadIcon />,
      version: "odoo_16",
      ruta: "/sig-mx",
      menuKey: "descargas",
      funcion: "ver",
    },
  ];

  // === Filtros de permisos — preservado ===
  const checkPermission = (item: MenuItemDef): boolean => {
    if (item.requiresPermission === false) return true;
    if (item.ruta && item.menuKey && item.funcion) {
      return hasPermission(item.ruta, item.menuKey, item.funcion);
    }
    return false;
  };

  const visibleMenuItems = menuItems.filter(checkPermission);
  const visibleVentasItems = ventasItems.filter(checkPermission);
  const visibleMercadoLibreItems = mercadoLibreItems.filter(checkPermission);
  const visibleComprasItems = comprasItems.filter(checkPermission);
  const visibleDistribucionItems = distribucionItems.filter(checkPermission);
  const visibleReportesDistribucionItems = reportesDistribucionItems.filter(checkPermission);
  const visibleUsuariosItems = usuariosItems.filter(checkPermission);
  const visibleOdooItems = odooItems.filter(checkPermission);
  const visibleSigMxItems = sigMxItems.filter((item) =>
    hasPermission(item.ruta, item.menuKey, item.funcion),
  );

  // === Navegación ===
  const handleClose = (): void => {
    if (variant === "temporary") onToggle?.(false);
  };

  const handleNavigation = (path: string): void => {
    navigate(path);
    handleClose();
  };

  // Sig-mx: pedir token efímero y disparar la descarga nativa del navegador.
  // Si el dump aún no existe, el backend lo genera en background y aquí se
  // informa al usuario que reintente en unos minutos.
  //
  // La descarga se dispara con un <a download> y NO con window.location: los
  // dumps pesan cientos de MB y navegar el documento a la URL de descarga
  // abortaba la transferencia (2026-08-20: tres intentos cortados en el mismo
  // byte, 1,047,515 de 573 MB, sin dejar ni .crdownload).
  const handleSigMxDownload = async (
    version: SigMxDumpVersion,
  ): Promise<void> => {
    if (sigMxBusy) return;
    setSigMxBusy(version);
    try {
      const result = await sigMxService.requestDownloadToken(version);
      if (result.ready && result.token) {
        const enlace = document.createElement("a");
        enlace.href = sigMxService.buildDownloadUrl(result.token);
        // El nombre real lo fija Content-Disposition del backend; esto es el
        // respaldo si el navegador no lo recibe.
        enlace.download = result.fileName ?? `${version}.sql.gz`;
        enlace.rel = "noopener";
        enlace.style.display = "none";
        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();
        setSigMxSnackbar({
          message: `Descarga de ${version} iniciada`,
          severity: "success",
        });
      } else if (result.error) {
        setSigMxSnackbar({
          message: `El intento anterior de generar la copia de ${version} falló: ${result.error}. Se está reintentando.`,
          severity: "warning",
        });
      } else {
        setSigMxSnackbar({
          message: `La copia de ${version} se está generando. Intenta de nuevo en unos minutos.`,
          severity: "info",
        });
      }
    } catch {
      setSigMxSnackbar({
        message: `No se pudo iniciar la descarga de ${version}.`,
        severity: "error",
      });
    } finally {
      setSigMxBusy(null);
    }
  };

  const isActiveRoute = (path: string): boolean => {
    // Coincidencia exacta o por segmento ("/ventas" activa "/ventas/x" pero
    // NO "/ventas-b2b", que solo comparte prefijo de texto).
    return (
      location.pathname === path ||
      (path !== "/" && location.pathname.startsWith(`${path}/`))
    );
  };

  const userInitials = (user?.email ?? "")
    .split("@")[0]
    ?.slice(0, 2)
    .toUpperCase() || "?";

  // === Contenido del drawer (dark theme) ===
  const drawerContent = (
    <Stack
      sx={{
        height: "100%",
        backgroundColor: "#0e1320",
        color: "#cbd1de",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {/* Brand header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ padding: "14px 14px 16px" }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ cursor: "pointer" }}
          onClick={() => handleNavigation("/")}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background:
                "linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-blue-2) 100%)",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "-0.02em",
            }}
          >
            M
          </Box>
          <Stack sx={{ lineHeight: 1.1 }}>
            <Typography
              component="strong"
              sx={{ fontSize: "13px", fontWeight: 600, color: "#fff" }}
            >
              VillApps
            </Typography>
            <Typography
              component="small"
              sx={{ fontSize: "11px", color: "#8088a0" }}
            >
              MásXPieza · MxP
            </Typography>
          </Stack>
        </Stack>
        {variant === "temporary" && (
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="cerrar menú"
            sx={{
              color: "#8088a0",
              "&:hover": { backgroundColor: "#1a2033", color: "#fff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {/* Inicio */}
      {visibleMenuItems.map((item) => (
        <SidebarItem
          key={item.path}
          active={isActiveRoute(item.path)}
          icon={item.icon}
          label={item.title}
          onClick={() => handleNavigation(item.path)}
        />
      ))}

      {/* Analytics */}
      {visibleVentasItems.length > 0 && (
        <>
          <SidebarSection label="Analytics" />
          {visibleVentasItems.map((item) => (
            <SidebarItem
              key={item.path}
              active={isActiveRoute(item.path)}
              icon={item.icon}
              label={item.title}
              onClick={() => handleNavigation(item.path)}
            />
          ))}
        </>
      )}

      {/* Mercado Libre */}
      {visibleMercadoLibreItems.length > 0 && (
        <>
          <SidebarSection label="Mercado Libre" />
          {visibleMercadoLibreItems.map((item) => (
            <SidebarItem
              key={item.path}
              active={isActiveRoute(item.path)}
              icon={item.icon}
              label={item.title}
              onClick={() => handleNavigation(item.path)}
            />
          ))}
        </>
      )}

      {/* Compras */}
      {visibleComprasItems.length > 0 && (
        <>
          <SidebarSection label="Compras" />
          {visibleComprasItems.map((item) => (
            <SidebarItem
              key={item.path}
              active={isActiveRoute(item.path)}
              icon={item.icon}
              label={item.title}
              onClick={() => handleNavigation(item.path)}
            />
          ))}
        </>
      )}

      {/* Distribución */}
      {(visibleDistribucionItems.length > 0 ||
        visibleReportesDistribucionItems.length > 0) && (
        <>
          <SidebarSection label="Distribución" />
          {visibleDistribucionItems.map((item) => (
            <SidebarItem
              key={item.path}
              active={isActiveRoute(item.path)}
              icon={item.icon}
              label={item.title}
              onClick={() => handleNavigation(item.path)}
            />
          ))}
          {visibleReportesDistribucionItems.length > 0 && (
            <>
              <SidebarItem
                active={false}
                icon={<AssessmentIcon />}
                label="Reportes"
                expandable
                expanded={reportesDistribucionOpen}
                onClick={() =>
                  setReportesDistribucionOpen(!reportesDistribucionOpen)
                }
              />
              {reportesDistribucionOpen &&
                visibleReportesDistribucionItems.map((item) => (
                  <SidebarItem
                    key={item.path}
                    active={isActiveRoute(item.path)}
                    icon={item.icon}
                    label={item.title}
                    nested
                    onClick={() => handleNavigation(item.path)}
                  />
                ))}
            </>
          )}
        </>
      )}

      {/* Sistema */}
      {(visibleOdooItems.length > 0 ||
        visibleUsuariosItems.length > 0 ||
        visibleSigMxItems.length > 0) && (
        <>
          <SidebarSection label="Sistema" />
          {visibleOdooItems.length > 0 && (
            <>
              <SidebarItem
                active={false}
                icon={<CloudSyncIcon />}
                label="Odoo ERP"
                expandable
                expanded={odooOpen}
                onClick={() => setOdooOpen(!odooOpen)}
              />
              {odooOpen &&
                visibleOdooItems.map((item) => (
                  <SidebarItem
                    key={item.path}
                    active={isActiveRoute(item.path)}
                    icon={item.icon}
                    label={item.title}
                    nested
                    onClick={() => handleNavigation(item.path)}
                  />
                ))}
            </>
          )}
          {visibleUsuariosItems.length > 0 && (
            <>
              <SidebarItem
                active={false}
                icon={<AccountBoxIcon />}
                label="Usuarios"
                expandable
                expanded={usuariosOpen}
                onClick={() => setUsuariosOpen(!usuariosOpen)}
              />
              {usuariosOpen &&
                visibleUsuariosItems.map((item) => (
                  <SidebarItem
                    key={item.path}
                    active={isActiveRoute(item.path)}
                    icon={item.icon}
                    label={item.title}
                    nested
                    onClick={() => handleNavigation(item.path)}
                  />
                ))}
            </>
          )}
          {visibleSigMxItems.length > 0 && (
            <>
              <SidebarItem
                active={false}
                icon={<StorageIcon />}
                label="Sig-mx"
                expandable
                expanded={sigMxOpen}
                onClick={() => setSigMxOpen(!sigMxOpen)}
              />
              {sigMxOpen &&
                visibleSigMxItems.map((item) => (
                  <SidebarItem
                    key={item.version}
                    active={false}
                    icon={item.icon}
                    label={
                      sigMxBusy === item.version ? "Solicitando…" : item.title
                    }
                    nested
                    onClick={() => void handleSigMxDownload(item.version)}
                  />
                ))}
            </>
          )}
        </>
      )}

      <Box sx={{ flex: 1 }} />

      {/* Footer con usuario */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          padding: "14px 14px 12px",
          borderTop: "1px solid #1a2033",
          backgroundColor: "#0e1320",
        }}
      >
        <Tooltip title={user?.email ?? "Usuario"} arrow placement="top">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #4a5570, #2a3145)",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontSize: "11px",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {userInitials}
          </Box>
        </Tooltip>
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "12px",
              color: "#fff",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {(user?.email ?? "").split("@")[0] || "Usuario"}
          </Typography>
          <Typography sx={{ fontSize: "10.5px", color: "#6e768a" }}>
            Owner
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <>
      <Drawer
        variant={variant}
        anchor="left"
        open={variant === "permanent" ? true : isOpen}
        onClose={handleClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: SIDEBAR_WIDTH,
            backgroundColor: "#0e1320",
            color: "#cbd1de",
            borderRight: "1px solid #1a2033",
            boxShadow: variant === "permanent" ? "none" : "0 4px 20px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        {drawerContent}
      </Drawer>
      {/* Feedback de descargas Sig-mx */}
      <Snackbar
        open={sigMxSnackbar !== null}
        autoHideDuration={6000}
        onClose={() => setSigMxSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={sigMxSnackbar?.severity ?? "info"}
          variant="filled"
          onClose={() => setSigMxSnackbar(null)}
        >
          {sigMxSnackbar?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
